/**
 * API client del destil (Fase 27).
 *
 * - `fetchSessionDistill(sessionId)`: resumen del job de una sesión.
 * - `fetchDistillHistory(filters)`: historial global con paginación cursor.
 * - `redistillSession(sessionId, mode)`: encola A/B re-run.
 */
import { get, postJsonAdmin } from './_core.js';

export interface DistillerSummary {
  name: string;
  durationMs: number;
  costNano: string | null;
  /** Raw provider cost (sin markup). Migration 0061 / Fase 34. */
  providerCostNano?: string | null;
  markupBps?: number | null;
  spanId: string;
}

export interface DistillToolCallSummary {
  name: string;
  count: number;
  totalDurationMs: number;
}

export interface SessionDistillSummary {
  mode: 'oneshot' | 'agentic';
  totalDurationMs: number;
  totalCostNano: string;
  totalCostRawNano?: string;
  factsAccepted: number;
  delayMs: number | null;
  startedAt: string;
  endedAt: string;
  jobSpanId: string;
  distillers: DistillerSummary[];
  toolsCalled: DistillToolCallSummary[];
}

export interface SessionDistillResponse {
  distill: SessionDistillSummary | null;
}

export async function fetchSessionDistill(
  sessionId: string,
): Promise<SessionDistillResponse> {
  const res = await get<SessionDistillResponse>(
    `/admin/sessions/${encodeURIComponent(sessionId)}/distill`,
  );
  return res.data;
}

export interface DistillHistoryItem {
  jobSpanId: string;
  traceId: string;
  sessionId: string | null;
  userId: string | null;
  userEmail: string | null;
  moduleSlug: string | null;
  mode: 'oneshot' | 'agentic';
  startedAt: string;
  endedAt: string;
  durationMs: number;
  totalCostNano: string;
  totalCostRawNano?: string;
  factsAccepted: number | null;
}

export interface DistillHistoryFilters {
  mode?: 'oneshot' | 'agentic';
  limit?: number;
  before?: string;
}

export interface DistillHistoryResponse {
  items: DistillHistoryItem[];
  nextCursor: string | null;
}

export async function fetchDistillHistory(
  filters: DistillHistoryFilters = {},
): Promise<DistillHistoryResponse> {
  const params = new URLSearchParams();
  if (filters.mode) params.set('mode', filters.mode);
  if (filters.limit) params.set('limit', String(filters.limit));
  if (filters.before) params.set('before', filters.before);
  const qs = params.toString();
  const res = await get<DistillHistoryItem[]>(`/admin/distill${qs ? `?${qs}` : ''}`);
  return {
    items: res.data,
    nextCursor: res.meta?.nextCursor ?? null,
  };
}

export interface RedistillResult {
  jobId: string;
  mode: 'oneshot' | 'agentic';
}

export async function redistillSession(
  sessionId: string,
  mode: 'oneshot' | 'agentic',
): Promise<RedistillResult> {
  return postJsonAdmin<RedistillResult>(
    `/admin/sessions/${encodeURIComponent(sessionId)}/redistill?mode=${mode}`,
    {},
  );
}
